import { FileUpload } from '@getsynapse/design-system';
import { intakeQuestionWrapper } from 'utils/customTypes';
import { useState } from 'react';
import intl from 'react-intl-universal';
import config from 'config/Config';
import { PickerFileMetadata } from 'filestack-js';
import get from 'lodash/get';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SYSTEM_MAX_BYTES, formatLimit } from 'utils/fileSize';

const T = 'SETTINGS_PAGE.FORMS.DESIGN.CUSTOM_QUESTIONS.FILE.MAX_SIZE_INTAKE';

const FileUploadQuestion = ({
  question,
  handler,
  disabled,
}: intakeQuestionWrapper) => {
  const [currentFiles, setCurrentFiles] = useState<Array<PickerFileMetadata>>(
    question.data.value || []
  );
  const { requestCustomMaxFileSizeLegacy = false } = useFlags<{
    requestCustomMaxFileSizeLegacy?: boolean;
  }>();

  const effectiveBytes: number =
    get(question, 'data.maxFileSizeBytes') ?? SYSTEM_MAX_BYTES;
  const limitLabel = formatLimit(effectiveBytes);
  const oversizeFiles = requestCustomMaxFileSizeLegacy
    ? currentFiles.filter(
        (file: PickerFileMetadata) =>
          typeof file.size === 'number' && file.size > effectiveBytes
      )
    : [];

  return (
    <div>
      {oversizeFiles.length > 0 && (
        <p
          data-testid='file-upload__oversize-warning'
          className='text-sm text-error-dark mb-1'
        >
          {oversizeFiles.map((file) => file.filename).join(', ')}{' '}
          {intl.get(`${T}.OVER`, { limit: limitLabel })}
        </p>
      )}
      {requestCustomMaxFileSizeLegacy && (
        <p
          data-testid='file-upload__limit'
          className='text-sm text-neutral mb-1'
        >
          {intl.get(`${T}.MAX`, { limit: limitLabel })}
        </p>
      )}
      <FileUpload
        uploadInstructions={get(question, 'data.placeholder')}
        config={{
          ...config.get('fileStack'),
          apiKey: process.env.REACT_APP_FILESTACK_API_KEY,
          security: {
            policy: process.env.REACT_APP_FILESTACK_POLICY,
            signature: process.env.REACT_APP_FILESTACK_SIGNATURE,
          },
          ...(requestCustomMaxFileSizeLegacy
            ? { maxSize: effectiveBytes }
            : {}),
        }}
        onFileUploadHandle={(files: Array<PickerFileMetadata>) => {
          setCurrentFiles((prevFiles: Array<PickerFileMetadata>) => {
            const newFiles = prevFiles.concat(files);
            handler(question, newFiles, 'data.value');

            return newFiles;
          });
        }}
        onFileRemoveHandle={(handle: string) => {
          setCurrentFiles((prevFiles: Array<PickerFileMetadata>) => {
            const newFiles = prevFiles.filter(
              (file: PickerFileMetadata) => file.handle !== handle
            );
            handler(question, newFiles, 'data.value');

            return newFiles;
          });
        }}
        files={currentFiles}
        disabled={disabled}
      />
    </div>
  );
};

export default FileUploadQuestion;
